import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { AdminConfig } from '../../lib/admin';
import LoadingSpinner from '../../components/LoadingSpinner';
import { trackError, createErrorMeta } from '../../lib/telemetry';

interface DashboardStats {
  totalUsers: number;
  abuseQueueCount: number;
  recentMetrics?: {
    date: string;
    totalUsers: number;
    totalViews: number;
    totalActions: number;
  };
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check auth token claims
    const checkAuthClaims = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const tokenResult = await user.getIdTokenResult();
          console.log('[AdminConsole] Auth token claims:', {
            isAdmin: tokenResult.claims.isAdmin,
            hasPro: tokenResult.claims.hasPro,
            hasArchive: tokenResult.claims.hasArchive,
            allClaims: tokenResult.claims,
          });
        }
      } catch (err) {
        console.error('[AdminConsole] Failed to get token claims:', err);
      }
    };

    checkAuthClaims();
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[AdminConsole] Starting dashboard load...');

      // Load stats in parallel
      const [usersCount, abuseCount, configData, recentMetrics] = await Promise.all([
        getTotalUsers(),
        getAbuseQueueCount(),
        getGlobalConfig(),
        getRecentMetrics(),
      ]);

      console.log('[AdminConsole] Dashboard data loaded:', {
        usersCount,
        abuseCount,
        configData,
        hasRecentMetrics: !!recentMetrics,
      });

      setStats({
        totalUsers: usersCount,
        abuseQueueCount: abuseCount,
        recentMetrics,
      });

      setConfig(configData);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dashboard data';
      setError(errorMessage);

      console.error('[AdminConsole] Dashboard load failed:', {
        error,
        code: error.code,
        message: error.message,
        stack: error.stack,
      });

      trackError(
        'Admin dashboard load failed',
        createErrorMeta('ui', 'ADMIN_DASHBOARD_LOAD', 'S3', 'degraded', '24h', {
          userAction: 'admin_dashboard_view',
        }),
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const getTotalUsers = async (): Promise<number> => {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    return snapshot.size;
  };

  const getAbuseQueueCount = async (): Promise<number> => {
    // Query _flagged_users collection for pending reviews
    const flaggedUsersRef = collection(db, '_flagged_users');
    const pendingQuery = query(flaggedUsersRef, where('status', '==', 'pending_review'));
    const snapshot = await getDocs(pendingQuery);
    return snapshot.size;
  };

  const getGlobalConfig = async (): Promise<AdminConfig> => {
    const configDoc = await getDoc(doc(db, '_config', 'global'));
    if (configDoc.exists()) {
      return configDoc.data() as AdminConfig;
    }
    // Return defaults if no config set (1GB PRODUCTION LIMIT)
    return {
      maxDailyUploadBytes: 1_000_000_000, // 1GB default
      maxDailyUploadCount: 200, // 200 uploads default
      archiveYears: 6,
      enableStripeTax: false,
      flags: {
        allowSignups: true,
        abuseQueue: true,
        metrics: true,
      },
    };
  };

  const getRecentMetrics = async () => {
    try {
      const metricsDoc = await getDoc(doc(db, 'metrics', 'daily'));
      if (metricsDoc.exists()) {
        const data = metricsDoc.data();
        const lastProcessedDate = data?.lastProcessedDate;
        if (lastProcessedDate) {
          const dateMetricsDoc = await getDoc(
            doc(db, 'metrics', 'daily', 'dates', lastProcessedDate)
          );
          if (dateMetricsDoc.exists()) {
            return dateMetricsDoc.data() as any;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load recent metrics:', error);
    }
    return null;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading admin dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-800 mb-2">Error Loading Dashboard</h2>
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Alert Notification for Flagged Users */}
      {stats && stats.abuseQueueCount > 0 && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-sm animate-pulse">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                ⚠️ Abuse Queue Requires Attention
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <strong>{stats.abuseQueueCount}</strong> user
                  {stats.abuseQueueCount > 1 ? 's' : ''} flagged for excessive upload activity.{' '}
                  <Link to="/admin/abuse" className="font-medium underline hover:text-red-900">
                    Review now →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Console</h1>
        <p className="mt-2 text-gray-600">
          Manage users, monitor system health, and configure global settings.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Users */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.totalUsers || 0}</p>
            </div>
            <div className="h-12 w-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Abuse Queue */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Abuse Queue</p>
              <p className="text-3xl font-bold text-red-600">{stats?.abuseQueueCount || 0}</p>
            </div>
            <div className="h-12 w-12 bg-red-50 rounded-lg flex items-center justify-center relative">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              {/* Badge */}
              {stats && stats.abuseQueueCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-red-600 rounded-full animate-pulse">
                  {stats.abuseQueueCount > 9 ? '9+' : stats.abuseQueueCount}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Daily Upload Limit */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Daily Upload Limit</p>
              <p className="text-3xl font-bold text-green-600">
                {formatBytes(config?.maxDailyUploadBytes || 1_000_000_000)}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-50 rounded-lg flex items-center justify-center">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Recent Metrics */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Recent Activity</p>
              <p className="text-3xl font-bold text-purple-600">
                {stats?.recentMetrics ? `${stats.recentMetrics.totalViews}` : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {stats?.recentMetrics
                  ? `Views on ${stats.recentMetrics.date}`
                  : 'No recent metrics'}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-50 rounded-lg flex items-center justify-center">
              <svg
                className="h-6 w-6 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Users Management */}
        <Link
          to="/admin/users"
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Search users by email or UID, view details, and manage account flags.
          </p>
          <div className="flex items-center text-blue-600 text-sm font-medium">
            <span>Manage Users</span>
            <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>

        {/* Abuse Queue */}
        <Link
          to="/admin/abuse"
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-red-50 rounded-lg flex items-center justify-center mr-3">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Abuse Queue</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Review accounts flagged for excessive upload activity and manage holds.
          </p>
          <div className="flex items-center text-red-600 text-sm font-medium">
            <span>Review Queue</span>
            <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>

        {/* Metrics */}
        <Link
          to="/admin/metrics"
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center mr-3">
              <svg
                className="h-5 w-5 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Usage Metrics</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Build and view daily usage statistics and system analytics.
          </p>
          <div className="flex items-center text-purple-600 text-sm font-medium">
            <span>View Metrics</span>
            <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>

        {/* Config Management */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center mr-3">
              <svg
                className="h-5 w-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">System Config</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Configure global upload limits, feature flags, and system settings.
          </p>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Max Daily Uploads:</span>
              <span className="font-medium">{config?.maxDailyUploadCount || 200}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Archive Years:</span>
              <span className="font-medium">{config?.archiveYears || 6}</span>
            </div>
          </div>
        </div>

        {/* Feature Flags */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-yellow-50 rounded-lg flex items-center justify-center mr-3">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Feature Flags</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Control system-wide feature availability and behavior.
          </p>
          <div className="space-y-2">
            {config?.flags &&
              Object.entries(config.flags).map(([flag, enabled]) => (
                <div key={flag} className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 capitalize">
                    {flag.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                  </span>
                  <span className={`font-medium ${enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-indigo-50 rounded-lg flex items-center justify-center mr-3">
              <svg
                className="h-5 w-5 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Common administrative tasks and maintenance operations.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/admin/metrics')}
              className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
            >
              Build Today's Metrics
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
            >
              Refresh Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
